import { Component, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { EmployeeAdvance, EmployeeAdvanceStatus } from '../../../models/employee-advance/employee-advance';
import { EmployeeAdvanceProxyService } from '../../../proxies/employee-advance.proxy.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

/** The latest advances shown on a trip's page. */
const TRIP_ADVANCES_LIMIT = 10;

/** The employee advances of one trip (#5789), newest first, for its details page. */
@Component({
  selector: 'app-trip-advances',
  templateUrl: './trip-advances.component.html',
})
export class TripAdvancesComponent {
  private readonly proxy = inject(EmployeeAdvanceProxyService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  readonly tripReference = input.required<string>();

  readonly isLoading = signal(false);
  readonly advances = signal<EmployeeAdvance[]>([]);

  constructor() {
    effect(() => this.load(this.tripReference()), { allowSignalWrites: true });
  }

  private load(tripReference: string): void {
    this.isLoading.set(true);
    this.proxy
      .search({ tripReference }, 1, TRIP_ADVANCES_LIMIT, null, -1)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((response) => this.advances.set(response.data?.items ?? []));
  }

  open(advance: EmployeeAdvance): void {
    this.router.navigate(['/employee-advance-details'], { queryParams: { id: advance.id } });
  }

  statusLabel(status: EmployeeAdvanceStatus): string {
    return cashDocumentStatusLabel(status, (key, fallback) => {
      const value = this.translate.instant(key);
      return value === key ? fallback : value;
    });
  }

  statusSeverity(status: EmployeeAdvanceStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  getRow(rowData: unknown): EmployeeAdvance {
    return rowData as EmployeeAdvance;
  }
}
